import React, { useState, useEffect } from 'react';
import { FaUserMd, FaCalendarAlt, FaClock, FaFileUpload, FaCheckCircle, FaExclamationTriangle, FaFilePdf, FaImage, FaTrash, FaDownload, FaQrcode, FaRupeeSign, FaHistory } from 'react-icons/fa';
import QR from '../assets/QR.jpeg';
// Interfaces
interface Category { id: number; name: string; parentId: number | null; }
interface TimeSlot { time: string; isBooked: boolean; }
interface ExistingAppointment {
  id: string; booking_id: string; appointmentDate: string; appointmentTime: string;
  department: string; fullName: string; age: string; gender: string; symptoms: string;
  email: string; address: string; dob: string; phone: string; report_url: string | null;
}

type ApptMode = 'NEW' | 'EDIT' | 'MISSED' | 'FOLLOWUP';

const BookAppointment: React.FC = () => {
  // Form States
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [department, setDepartment] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [address, setAddress] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  
  // File Upload States
  const [reportFile, setReportFile] = useState<File | null>(null);
  const [existingReportUrl, setExistingReportUrl] = useState<string | null>(null);
  const [removeReport, setRemoveReport] = useState(false);

  // App & Logic States
  const [departments, setDepartments] = useState<Category[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [apptMode, setApptMode] = useState<ApptMode>('NEW');
  const [maxAllowedDate, setMaxAllowedDate] = useState<string | undefined>(undefined);
  
  // Modal States
  const [editApptId, setEditApptId] = useState('');
  const [existingAppt, setExistingAppt] = useState<ExistingAppointment | null>(null);
  const [showExistingModal, setShowExistingModal] = useState(false);
  const [showPastModal, setShowPastModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [successData, setSuccessData] = useState<{show: boolean; id: string, message: string}>({ show: false, id: '', message: '' });

  // Cancel & Payment States
  const [cancelReason, setCancelReason] = useState('');
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [timeLeft, setTimeLeft] = useState(600);

  const appointmentFee = 1100;
  const todayStr = new Date().toISOString().split("T")[0];
  // const backendUrl = 'http://localhost:8483'; 
  const backendUrl = 'https://www.ayushvatika.com'; 

  // --- Timer Logic ---
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (showPaymentModal && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    } else if (timeLeft === 0 && showPaymentModal) {
      setShowPaymentModal(false); setPaymentProof(null);
      alert("Payment session expired. Please try submitting again.");
    }
    return () => clearInterval(timer);
  }, [showPaymentModal, timeLeft]);

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const formatTime12Hour = (time24: string) => {
    if (!time24) return '';
    const [hour, min] = time24.split(':');
    const h = parseInt(hour, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${min} ${ampm}`;
  };

  const formatDateDDMMYYYY = (dateYMD: string) => {
    if (!dateYMD) return '';
    const [y, m, d] = dateYMD.split('-');
    return `${d}/${m}/${y}`;
  };

  const resetForm = () => {
    setMobile(''); setEmail(''); setFullName(''); setDob(''); setAge('');
    setGender(''); setDepartment(''); setSymptoms(''); setAddress('');
    setAppointmentDate(''); setSelectedTime(''); setReportFile(null); 
    setExistingReportUrl(null); setRemoveReport(false); setTimeSlots([]); 
    setUserId(null); setApptMode('NEW'); setMaxAllowedDate(undefined);
    setEditApptId(''); setExistingAppt(null); setPaymentProof(null); 
    setShowPaymentModal(false); setSuccessData({ show: false, id: '', message: '' });
    const fileInput = document.getElementById('report-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(`https://www.ayushvatika.com/api/categories`);
        const data: Category[] = await response.json();
        setDepartments(data.filter((cat: Category) => cat.parentId === null));
      } catch (error) { console.error(error); }
    };
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (dob) {
      const birthDate = new Date(dob);
      let calcAge = new Date().getFullYear() - birthDate.getFullYear();
      const m = new Date().getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && new Date().getDate() < birthDate.getDate())) calcAge--;
      setAge(calcAge.toString());
    }
  }, [dob]);

  useEffect(() => {
    if (appointmentDate) fetchSlotsForDate(appointmentDate);
  }, [appointmentDate]);

  const fetchSlotsForDate = async (date: string) => {
    try {
      const response = await fetch(`${backendUrl}/api/appointments/slots?date=${date}`);
      setTimeSlots(await response.json());
      setSelectedTime(''); 
    } catch (error) { console.error(error); }
  };

  const checkExistingUser = async () => {
    if (mobile.length !== 10) return;
    setIsLoadingUser(true);
    try {
      const response = await fetch(`${backendUrl}/api/users/check?mobile=${mobile}`);
      const data = await response.json();
      
      if (data.exists) {
        if (data.existingAppointment && apptMode === 'NEW') {
            setExistingAppt(data.existingAppointment);
            
            // TIME CHECK LOGIC
            const apptDateTime = new Date(`${data.existingAppointment.appointmentDate}T${data.existingAppointment.appointmentTime}:00`);
            const now = new Date();
            
            const oneMonthLater = new Date(apptDateTime);
            oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);

            if (apptDateTime > now) {
                // Future active appointment
                setShowExistingModal(true);
            } else if (now <= oneMonthLater) {
                // Past appointment within 1 month (Eligible for free Reschedule/Follow-up)
                setShowPastModal(true);
            } else {
                // > 1 Month (Must book new paid appointment)
                fillBasicUserData(data.user);
            }
        } else if (data.user && apptMode === 'NEW') {
            fillBasicUserData(data.user);
        }
      } else { setUserId(null); }
    } catch (error) { console.error(error); } 
    finally { setIsLoadingUser(false); }
  };

  const fillBasicUserData = (userData: any) => {
      if(!userData) return;
      setFullName(`${userData.firstName || ''} ${userData.lastName || ''}`.trim());
      setUserId(userData.id);
      if (userData.email && !userData.email.includes('@ayushvatika.local')) setEmail(userData.email);
  };

  const handleEditClick = () => {
    if(existingAppt) {
      prefillExistingData();
      setAppointmentDate(existingAppt.appointmentDate);
      setSelectedTime(existingAppt.appointmentTime); 
      setApptMode('EDIT');
      setShowExistingModal(false);
      fetchSlotsForDate(existingAppt.appointmentDate);
    }
  };

  const handlePastAction = (mode: 'MISSED' | 'FOLLOWUP') => {
      if(existingAppt) {
          prefillExistingData();
          setApptMode(mode);
          
          // Set Max Date to 1 Month from original appointment
          const limitDate = new Date(existingAppt.appointmentDate);
          limitDate.setMonth(limitDate.getMonth() + 1);
          setMaxAllowedDate(limitDate.toISOString().split("T")[0]);

          setShowPastModal(false);
          setAppointmentDate(''); // Force them to pick a new date
          setSelectedTime('');
      }
  };

  const prefillExistingData = () => {
      if(!existingAppt) return;
      setFullName(existingAppt.fullName); setMobile(existingAppt.phone);
      setEmail(existingAppt.email || ''); setDob(existingAppt.dob);
      setAge(existingAppt.age); setGender(existingAppt.gender);
      setAddress(existingAppt.address || ''); setDepartment(existingAppt.department);
      setSymptoms(existingAppt.symptoms); setExistingReportUrl(existingAppt.report_url);
      setEditApptId(existingAppt.id); setRemoveReport(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (apptMode === 'NEW') {
        setShowPaymentModal(true); setTimeLeft(600); 
    } else {
        await submitToBackend(); // Edit, Missed, or Follow-up skip payment
    }
  };

  const submitToBackend = async () => {
    const formData = new FormData();
    formData.append('mobile', mobile); formData.append('email', email);
    formData.append('fullName', fullName); formData.append('dob', dob);
    formData.append('age', age); formData.append('gender', gender);
    formData.append('address', address); formData.append('department', department); 
    formData.append('symptoms', symptoms); formData.append('appointmentDate', appointmentDate); 
    formData.append('appointmentTime', selectedTime);
    
    if (userId) formData.append('userId', userId);
    if (reportFile) formData.append('report', reportFile);
    
    if (apptMode !== 'NEW') {
        formData.append('appointmentId', editApptId);
        formData.append('removeReport', removeReport.toString());
        // Custom flag for follow-ups
        if (apptMode === 'FOLLOWUP') formData.append('isFollowUp', 'true');
    } else if (paymentProof) {
        formData.append('paymentProof', paymentProof);
    }

    const url = apptMode === 'NEW' ? `${backendUrl}/api/appointments/book-pending` : `${backendUrl}/api/appointments/update`;
    const method = apptMode === 'NEW' ? 'POST' : 'PUT';

    try {
      const response = await fetch(url, { method, body: formData });
      if(response.ok) {
         setShowPaymentModal(false);
         const data = await response.json();
         let msg = 'Appointment Updated!';
         if(apptMode === 'NEW') msg = 'Payment Proof Submitted Successfully!';
         if(apptMode === 'FOLLOWUP') msg = 'Follow-up Appointment Booked!';
         if(apptMode === 'MISSED') msg = 'Missed Appointment Rescheduled!';

         setSuccessData({ show: true, id: apptMode === 'NEW' ? 'Pending Approval' : data.bookingId, message: msg });
      } else { alert('Operation failed. Please try again.'); }
    } catch (error) { console.error(error); }
  };

  const handleCancelSubmit = async () => {
    if (cancelMessage.length < 15) { setCancelError("Message must be at least 15 characters."); return; }
    try {
      const response = await fetch(`${backendUrl}/api/appointments/cancel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId: existingAppt?.id, bookingId: existingAppt?.booking_id, fullName: existingAppt?.fullName, phone: mobile, reason: cancelReason, message: cancelMessage })
      });
      if(response.ok) {
          setShowCancelModal(false);
          setSuccessData({ show: true, id: existingAppt?.booking_id || 'N/A', message: 'Appointment Cancelled Successfully!' });
      } else { alert("Failed to cancel."); }
    } catch (error) { console.error(error); }
  };

  const isSlotAvailable = (slot: TimeSlot) => {
    if (slot.isBooked && !(apptMode === 'EDIT' && slot.time === existingAppt?.appointmentTime && appointmentDate === existingAppt?.appointmentDate)) return false;
    if (appointmentDate === todayStr) {
      const minAllowedTime = new Date(new Date().getTime() + 2 * 60 * 60 * 1000); 
      const [hours, minutes] = slot.time.split(':').map(Number);
      const slotTimeObj = new Date(); slotTimeObj.setHours(hours, minutes, 0, 0);
      return slotTimeObj >= minAllowedTime;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) { setReportFile(e.target.files[0]); setRemoveReport(false); }
  };

  const handlePaymentProofChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) setPaymentProof(e.target.files[0]);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8 relative">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden border border-green-100">
        
        <div className="bg-green-700 px-6 py-4 text-white flex items-center gap-3">
          <FaUserMd className="text-3xl" />
          <div>
            <h2 className="text-2xl font-bold font-['Pacifico'] tracking-wide">
                {apptMode === 'EDIT' ? 'Modify Appointment' : apptMode === 'MISSED' ? 'Reschedule Missed Appointment' : apptMode === 'FOLLOWUP' ? 'Book Follow-up Report' : 'Book Your Appointment'}
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 relative">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Patient Contact</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number *</label>
                <input type="tel" required maxLength={10} value={mobile} onChange={(e) => setMobile(e.target.value)} onBlur={checkExistingUser} disabled={apptMode !== 'NEW'} className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none disabled:bg-gray-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
              </div>
            </div>
            {isLoadingUser && <p className="text-xs text-green-600 mt-2 font-medium animate-pulse">Checking records...</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Address *</label>
              <input type="text" required value={address} onChange={(e) => setAddress(e.target.value)} placeholder="House No, Street, City" className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
              <input type="date" required value={dob} max={todayStr} onChange={(e) => setDob(e.target.value)} className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Age</label>
              <input type="text" value={age ? `${age} Years` : ''} readOnly className="w-full px-4 py-2 border bg-gray-100 rounded-md text-gray-500 cursor-not-allowed" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
              <select required value={gender} onChange={(e) => setGender(e.target.value)} className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 bg-white">
                <option value="">Select Gender</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
              <select required value={department} onChange={(e) => setDepartment(e.target.value)} className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 bg-white">
                <option value="">Select Department</option>
                {departments.map((dept) => <option key={dept.id} value={dept.name}>{dept.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Symptoms / Issue Description *</label>
            <textarea required rows={3} value={symptoms} onChange={(e) => setSymptoms(e.target.value)} className="w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none"></textarea>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center gap-2"><FaCalendarAlt /> Schedule Appointment</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Appointment Date *</label>
              <input type="date" required value={appointmentDate} min={todayStr} max={maxAllowedDate} onChange={(e) => setAppointmentDate(e.target.value)} className="w-full md:w-1/2 px-4 py-2 border rounded-md focus:ring-2 focus:ring-green-500" />
              {maxAllowedDate && <p className="text-xs text-orange-600 mt-1 font-semibold">Free rescheduling valid until: {formatDateDDMMYYYY(maxAllowedDate)}</p>}
            </div>
            {appointmentDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"><FaClock /> Available Time Slots</label>
                {timeSlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {timeSlots.map((slot) => {
                      const available = isSlotAvailable(slot);
                      return (
                        <button key={slot.time} type="button" disabled={!available} onClick={() => setSelectedTime(slot.time)}
                          className={`py-2 px-1 text-sm font-medium rounded-md border transition-all duration-200 ${
                            !available ? 'bg-red-50 text-red-300 border-red-100 cursor-not-allowed line-through'
                            : selectedTime === slot.time ? 'bg-green-600 text-white border-green-700 shadow-md'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-green-500 hover:text-green-600'
                          }`}>{formatTime12Hour(slot.time)}</button>
                      );
                    })}
                  </div>
                ) : <p className="text-sm text-gray-500">Fetching available slots...</p>}
              </div>
            )}
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <FaFileUpload className="text-green-600" /> Upload Previous Report (Optional)
            </label>
            
            {apptMode !== 'NEW' && existingReportUrl && !removeReport && !reportFile && (
                <div className="mb-4 p-3 border rounded bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                        {existingReportUrl.endsWith('.pdf') ? <FaFilePdf className="text-red-500 text-lg"/> : <FaImage className="text-blue-500 text-lg"/>}
                        <span className="truncate max-w-[200px]">Attached Report</span>
                    </div>
                    <div className="flex gap-2">
                        <a href={`${backendUrl}${existingReportUrl}`} target="_blank" rel="noreferrer" className="p-2 text-blue-600 hover:bg-blue-100 rounded"><FaDownload /></a>
                        <button type="button" onClick={() => setRemoveReport(true)} className="p-2 text-red-600 hover:bg-red-100 rounded"><FaTrash /></button>
                    </div>
                </div>
            )}

            {(!existingReportUrl || removeReport || reportFile) && (
                <input id="report-upload" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 transition-all cursor-pointer" />
            )}
            <p className="text-xs text-gray-400 mt-2">Max file size: 5MB (PDF, JPG, PNG)</p>
          </div>

          <div className="pt-4">
            <button type="submit" disabled={!selectedTime} className={`w-full py-3 px-4 rounded-md text-white font-bold text-lg transition-colors ${!selectedTime ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 shadow-lg'}`}>
              {apptMode === 'NEW' ? 'Proceed to Payment' : apptMode === 'MISSED' ? 'Confirm Reschedule' : apptMode === 'FOLLOWUP' ? 'Confirm Follow-up' : 'Update Appointment'}
            </button>
            {apptMode !== 'NEW' && (
               <button type="button" onClick={resetForm} className="w-full mt-3 py-3 px-4 rounded-md text-gray-600 border font-bold text-lg hover:bg-gray-100">Cancel & Start Fresh</button>
            )}
          </div>
        </form>
      </div>

      {/* --- PAYMENT MODAL --- */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-green-700 p-5 text-white text-center relative">
              <h3 className="text-xl font-bold mb-1">Complete Your Payment</h3>
              <p className="text-green-100 text-sm">Ayush Vatika Private Limited</p>
              <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow animate-pulse">
                {formatTimer(timeLeft)}
              </div>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <p className="text-gray-500 text-sm mb-2">Scan the QR code to pay the consultation fee</p>
                <div className="flex justify-center items-center mb-2">
                  <FaRupeeSign className="text-3xl text-gray-800" />
                  <span className="text-4xl font-black text-gray-800">{appointmentFee}</span>
                </div>
                <div className="mx-auto w-48 h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden mb-2">
                   <img src={QR} alt="QR Code" className="w-full h-full object-contain" />
                </div>
                <p className="text-xs text-gray-400">Accepted: PhonePe, GPay, Paytm, etc.</p>
              </div>

              <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl mb-6">
                <label className="block text-sm font-bold text-blue-800 mb-2">Upload Payment Proof *</label>
                <input type="file" accept="image/*" required onChange={handlePaymentProofChange} className="block w-full text-sm text-blue-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-100 file:text-blue-700 hover:file:bg-blue-200 cursor-pointer" />
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setShowPaymentModal(false); setPaymentProof(null); }} className="w-1/3 bg-gray-200 text-gray-800 font-bold py-3 rounded-xl hover:bg-gray-300">Cancel</button>
                <button onClick={submitToBackend} disabled={!paymentProof} className={`w-2/3 text-white font-bold py-3 rounded-xl ${paymentProof ? 'bg-green-600 hover:bg-green-700 shadow-md' : 'bg-gray-400 cursor-not-allowed'}`}>Submit Proof</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 1. FUTURE ACTIVE APPOINTMENT POPUP */}
      {showExistingModal && existingAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 border-b pb-2">Active Appointment Found</h3>
            <div className="space-y-3 mb-6 text-sm text-gray-700">
                <div className="flex justify-between border-b pb-1"><span className="font-semibold text-gray-500">Booking ID</span><span className="text-green-700 font-bold">{existingAppt.booking_id || 'Pending Approval'}</span></div>
                <div className="flex justify-between border-b pb-1"><span className="font-semibold text-gray-500">Date</span><span>{formatDateDDMMYYYY(existingAppt.appointmentDate)}</span></div>
                <div className="flex justify-between border-b pb-1"><span className="font-semibold text-gray-500">Time</span><span>{formatTime12Hour(existingAppt.appointmentTime)}</span></div>
            </div>
            <div className="space-y-3">
                <button onClick={handleEditClick} className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700">Edit Details</button>
                <button onClick={() => { setShowExistingModal(false); setShowCancelModal(true); }} className="w-full bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700">Cancel Appointment</button>
                <button onClick={() => { setShowExistingModal(false); resetForm(); }} className="w-full bg-gray-200 text-gray-800 py-2 rounded-lg font-semibold hover:bg-gray-300">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* 2. PAST APPOINTMENT POPUP (MISSED OR FOLLOWUP) */}
      {showPastModal && existingAppt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border-t-8 border-orange-500">
            <div className="flex items-center gap-3 mb-4 border-b pb-2">
                <FaHistory className="text-3xl text-orange-500" />
                <h3 className="text-xl font-bold text-gray-900">Past Appointment Found</h3>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
                You had an appointment on <strong className="text-gray-800">{formatDateDDMMYYYY(existingAppt.appointmentDate)} at {formatTime12Hour(existingAppt.appointmentTime)}</strong>.
            </p>
            
            <div className="bg-orange-50 rounded-lg p-3 text-xs text-orange-800 mb-6 border border-orange-200">
                You are eligible for a free reschedule or a follow-up consultation valid until <strong>{formatDateDDMMYYYY(maxAllowedDate || '')}</strong> (1 Month limit).
            </div>

            <div className="space-y-3">
                <button onClick={() => handlePastAction('MISSED')} className="w-full bg-orange-500 text-white py-3 rounded-lg font-bold shadow hover:bg-orange-600 transition-all">
                    I Missed It - Reschedule (Free)
                </button>
                <button onClick={() => handlePastAction('FOLLOWUP')} className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold shadow hover:bg-blue-700 transition-all">
                    Attended - Book Follow-up (Free)
                </button>
                <button onClick={() => { setShowPastModal(false); resetForm(); }} className="w-full bg-gray-200 text-gray-800 py-2 rounded-lg font-semibold hover:bg-gray-300">
                    Cancel & Close
                </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. CANCEL CONFIRMATION POPUP */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-red-600 mb-4 border-b pb-2 flex items-center gap-2"><FaExclamationTriangle /> Cancel Appointment</h3>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancellation *</label>
            <select required value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className="w-full mb-4 px-3 py-2 border rounded-md focus:ring-red-500 focus:outline-none">
                <option value="">Select Reason</option><option value="Feeling better">Feeling better</option><option value="Scheduling conflict">Scheduling conflict</option><option value="Booked by mistake">Booked by mistake</option><option value="Other">Other</option>
            </select>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message (Min 15 chars) *</label>
            <textarea required rows={3} value={cancelMessage} onChange={(e) => {setCancelMessage(e.target.value); setCancelError('');}} className="w-full mb-1 px-3 py-2 border rounded-md focus:ring-red-500 focus:outline-none" placeholder="Please provide brief details..."></textarea>
            {cancelError && <p className="text-xs text-red-500 mb-4">{cancelError}</p>}
            <div className="flex gap-3 mt-4">
                <button onClick={() => setShowCancelModal(false)} className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg font-semibold hover:bg-gray-300">Back</button>
                <button onClick={handleCancelSubmit} disabled={!cancelReason || cancelMessage.length < 15} className="flex-1 bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700 disabled:bg-red-300">Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* 4. SUCCESS POPUP */}
      {successData.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 text-center">
            <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${successData.message.includes('Cancel') ? 'bg-red-100' : 'bg-green-100'}`}>
              <FaCheckCircle className={`h-10 w-10 ${successData.message.includes('Cancel') ? 'text-red-600' : 'text-green-600'}`} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{successData.message}</h3>
            <div className="bg-gray-50 rounded-xl p-4 mb-8 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold mb-1">Booking ID</p>
              <p className={`text-2xl font-black tracking-wider ${successData.id === 'Pending Approval' ? 'text-orange-500' : 'text-gray-700'}`}>{successData.id}</p>
            </div>
            <button onClick={resetForm} className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700">Done</button>
          </div>
        </div>
      )}

    </div>
  );
};

export default BookAppointment;